import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";

export const TeleprompterGoogleMeetTeamsES = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter para Google Meet y Teams: Domina tus Reuniones Profesionales
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Proyectar confianza y autoridad en reuniones virtuales es la nueva ventaja ejecutiva. <strong>Lee discretamente tus guiones y notas</strong> mientras mantienes un contacto visual perfecto.
        </p>

        <p className="text-slate-300 mb-8 leading-relaxed">
            En esta guía esencial para profesionales modernos, mostramos cómo transformar tu Google Meet o Microsoft Teams en un verdadero escenario corporativo con PromptNinja. Aprende la técnica de superposición invisible para mantener tus ojos fijos en la lente, asegurando que nunca pierdas el hilo del pensamiento u olvides un punto crucial en tu presentación. Descubre cómo nuestra herramienta online discreta te permite presentar con la confianza de un líder experimentado, transmitiendo presencia ejecutiva y dominio total del tema, sin que tu audiencia sepa que estás usando un teleprompter.
        </p>

        <div className="bg-blue-600/10 border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center mb-12 shadow-xl">
            <h3 className="text-2xl font-bold text-white mb-4">Mantén el Foco en lo que Importa</h3>
            <p className="text-slate-300 mb-6 font-medium">
                Habla directamente a la lente y transmite autoridad sin esfuerzo.
                <strong>PromptNinja</strong> es invisible para tu audiencia e indispensable para ti.
            </p>
            <a href="/?lang=es#app" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-blue-500/25">
                Abrir Teleprompter Discreto
            </a>
            <p className="mt-4 text-sm text-slate-400 font-medium">Mantén presencia ejecutiva al instante.</p>
        </div>

        <div className="bg-gradient-to-r from-red-900/30 to-green-900/30 p-8 rounded-xl border border-slate-700 mb-12">
            <h3 className="text-3xl font-bold text-white mb-6">La Batalla de la Atención: Notas vs Teleprompter</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/70 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> El "Lector de Notas"
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>👀 <strong>Mirada:</strong> Baja o se desvía cada 10s</li>
                        <li>📉 <strong>Percepción:</strong> "Está leyendo", "No sabe el tema"</li>
                        <li>🤝 <strong>Conexión:</strong> Constantemente rota</li>
                        <li>😰 <strong>Ansiedad:</strong> Alta (miedo a perderse en el papel)</li>
                        <li>📺 <strong>Resultado:</strong> Reunión aburrida, baja conversión</li>
                    </ul>
                </div>
                <div className="bg-slate-900/70 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> El "Líder Conectado" (PromptNinja)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>👀 <strong>Mirada:</strong> 100% en la lente de la webcam</li>
                        <li>📈 <strong>Percepción:</strong> Autoritario, confiado, presente</li>
                        <li>🤝 <strong>Conexión:</strong> Intensa (contacto visual virtual)</li>
                        <li>😌 <strong>Ansiedad:</strong> Cero (el texto está ahí, flotando)</li>
                        <li>📺 <strong>Resultado:</strong> Compromiso total, alta persuasión</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Contacto Visual Virtual = Poder. Quien domina la lente, domina la reunión.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">3 Configuraciones para Reuniones Online</h3>
        <div className="space-y-6 mb-12">
            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-indigo-500">
                <h3 className="text-xl font-bold text-indigo-400 mb-3">Setup #1: Monitor Único (Laptop) - "La Capa Invisible"</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Solo tu laptop con webcam integrada. Ideal para viajes o home office simple.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li>Abre Google Meet/Teams y maximiza la ventana.</li>
                    <li>Abre PromptNinja en una NUEVA ventana del navegador.</li>
                    <li>Redimensiona PromptNinja para que sea <strong>estrecho y transparente</strong>.</li>
                    <li>Posiciona esta ventana EXACTAMENTE en el centro superior, justo debajo del punto de la webcam física.</li>
                    <li><strong>El Truco:</strong> Lees texto que está a 2cm de la cámara. Para los espectadores, parece que los estás mirando a los ojos.</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal para:</strong> Scrums diarios, check-ins rápidos, nómadas digitales.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-blue-400 mb-3">Setup #2: Doble Monitor - "Centro de Comando"</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Laptop + Monitor Externo.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Monitor 1 (con Webcam):</strong> Aquí va PromptNinja. Centrado arriba.</li>
                    <li><strong>Monitor 2 (Lateral):</strong> Aquí van las caras de los participantes de Teams/Meet o tu presentación PowerPoint.</li>
                    <li><strong>Flujo:</strong> Presentas mirando al Monitor 1 (Cámara + Texto). Consultas reacciones en Monitor 2 rápidamente.</li>
                    <li>Esto evita el error clásico de mirar al monitor lateral (vista de perfil) mientras hablas a la cámara frontal.</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal para:</strong> Ventas complejas, clases online, gestión de equipos.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-400 mb-3">Setup #3: Entrevista de Trabajo (Modo Sigiloso)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Escenario:</strong> Te están entrevistando y quieres tener tus respuestas "STAR" a la mano sin parecer memorizado.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li>PromptNinja con 30% opacidad (casi invisible).</li>
                    <li>Texto en viñetas, no frases completas (para sonar natural).</li>
                    <li>Ventana de Meet DETRÁS del teleprompter, para ver la reacción del entrevistador A TRAVÉS del texto.</li>
                    <li>Usa auto-scroll muy lento o control manual vía teclado/mouse para evitar parecer robótico.</li>
                </ol>
                <p className="text-yellow-400 text-sm mt-3">⚠️ <strong>Tip Pro:</strong> No leas. Úsalo como "hoja de trucos de palabras clave" para desbloquear tu memoria.</p>
            </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg my-8 border-l-4 border-red-500">
            <h3 className="text-2xl font-bold text-white mb-4">🚨 El Peligro de Compartir Pantalla</h3>
            <p className="text-slate-300 mb-4">
                El error #1 que mata carreras: compartir la pantalla equivocada y mostrar tu guion (o tu WhatsApp) al cliente.
            </p>
            <SEOContentHowTo
                title=""
                schemaTitle="Cómo Compartir Pantalla de Forma Segura en Teams/Meet"
                totalTime="PT1M"
                tools={["Google Meet/Teams", "PromptNinja"]}
                steps={[
                    {
                        title: "Regla de Oro: Nunca 'Pantalla Completa'",
                        text: "Nunca uses la opción 'Compartir Pantalla Completa'. Esto muestra todo, incluido tu PromptNinja superpuesto."
                    },
                    {
                        title: "Usa 'Compartir Ventana'",
                        text: "En Meet/Teams, elige 'Una Ventana'. Selecciona SOLO el PowerPoint o PDF que quieres mostrar."
                    },
                    {
                        title: "El Truco",
                        text: "Como compartiste solo la ventana del PPT, PromptNinja puede sentarse físicamente FRENTE al PPT en tu pantalla. TÚ ves el teleprompter sobre la diapositiva. EL CLIENTE ve solo la diapositiva limpia."
                    }
                ]}
            />
            <p className="text-slate-300 mt-4 text-sm">
                Esto te permite tener "Subtítulos de Realidad Aumentada" sobre tu propia presentación, invisible para la audiencia.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">5 Errores Que Revelan Que Estás Leyendo</h3>
        <div className="space-y-4 mb-12">
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #1: Ojos de Ping-Pong (Lectura Rápida)</h4>
                <p className="text-slate-300 text-sm">
                    Tus ojos se mueven de izquierda a derecha rápidamente. En pantallas grandes, es obvio.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> Reduce el ANCHO de la ventana de PromptNinja a 300px-400px. Entre más estrecho el texto, menos se mueven tus ojos lateralmente.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #2: Reflejo del Monitor en Lentes</h4>
                <p className="text-slate-300 text-sm">
                    Fondo blanco genérico de Word se refleja en tus lentes. El scroll del texto es visible en el reflejo.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> PromptNinja tiene fondo NEGRO por defecto. Esto corta el 90% del reflejo. Ajusta el brillo del monitor al mínimo cómodo.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #3: Olvidar Parpadear y Sonreír</h4>
                <p className="text-slate-300 text-sm">
                    La "Cara de Lectura": ojos abiertos, parpadeo raro, cara seria. Aterrador en HD.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> Pon emojis 😄 o recordatorios [SONRÍE] y [RESPIRA] en medio del texto. Rompen patrones robóticos.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #4: Ventana Lejos de la Cámara</h4>
                <p className="text-slate-300 text-sm">
                    Teleprompter al fondo de la pantalla. Pareces mirar su barbilla o escritorio.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> El texto debe estar en la "Cima" de tu pantalla, lo más cerca posible del borde físico superior.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #5: Voz Monótona</h4>
                <p className="text-slate-300 text-sm">
                    Leer oraciones largas sin pausas naturales. Suena como síntesis de voz.
                    <span className="text-green-400 block mt-2">✅ <strong>Solución:</strong> Escribe como hablas, no como un email. Usa oraciones cortas. Usa MAYÚSCULAS para palabras que necesitan énfasis vocal.</span>
                </p>
            </div>
        </div>

        <div className="text-center bg-slate-800 p-8 rounded-lg my-10">
            <h3 className="text-3xl font-bold text-white mb-4">💼 Profesionaliza tu Home Office</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                No dejes que la tecnología se interponga en la conexión. Úsala para amplificar tu presencia.
            </p>
            <a
                href="/?lang=es#app"
                className="inline-block bg-gradient-to-r from-indigo-500 to-blue-600 font-bold py-3 px-8 rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                🚀 Preparar Reunión Ahora (Gratis)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Funciona en Chrome, Edge, Safari • Seguro y Privado</p>
        </div>

        <SEOContentFAQ
            title="Preguntas Frecuentes - Reuniones Online"
            items={[
                {
                    question: "¿Microsoft Teams o Google Meet detectan que uso otra ventana?",
                    answer: "No el contenido. Saben que no tienes su ventana 'enfocada' (activa), pero no saben QUÉ estás viendo. Para ellos, podrías estar mirando notas. PromptNinja es invisible para la API de la llamada."
                },
                {
                    question: "¿Cómo cambiar diapositivas y desplazar texto al mismo tiempo?",
                    answer: "Mejor no hacer ambas. Opción A: Auto-texto (Play con velocidad baja). Opción B: Usa Rueda del Mouse para texto y Teclado (Flechas) para diapositivas. Mantén foco del mouse en ventana de PromptNinja."
                },
                {
                    question: "¿Funciona con fondo borroso o fondo virtual?",
                    answer: "Sí, eso es procesado por la app de cámara/reunión. PromptNinja es solo una ventana en tu pantalla, no interfiere con el video de tu webcam. Te ayuda a MIRAR a la webcam."
                },
                {
                    question: "¿Qué pasa si se cae el internet?",
                    answer: "PromptNinja es PWA y funciona Offline. Si el internet cae a mitad de reunión, Meet/Teams se congela, pero tu guion sigue ahí, seguro, para cuando reconectes vía 4G."
                },
                {
                    question: "¿Puedo usar esto para entrevistas de trabajo?",
                    answer: "Sí, es un 'arma secreta' común. La clave es no escribir el discurso completo. Escribe palabras clave: 'PROYECTO X - RESULTADO 20% - LIDERAZGO ÁGIL'. Mira las palabras y habla natural. Si lees oraciones completas, RRHH entrenado lo notará."
                },
                {
                    question: "¿Necesito un monitor ultrawide?",
                    answer: "No, de hecho los monitores muy anchos pueden estorbar si pones el texto en la esquina. El secreto es siempre centrar el texto justo debajo de la cámara, sin importar el tamaño del monitor."
                },
                {
                    question: "¿Funciona en Zoom y Webex también?",
                    answer: "Sí, funciona sobre CUALQUIER app de videoconferencia. El principio es el mismo: ventana flotante superpuesta, cerca de la cámara, compartir ventana (no pantalla)."
                }
            ]}
        />
    </>
);
